import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../../lib/api';
import { Clock, AlertCircle, CheckCircle, XCircle, RefreshCw, Zap, Shield, Activity } from 'lucide-react';

interface CronJob {
  hook: string;
  name: string;
  description: string;
  critical: boolean;
  scheduled: boolean;
  health: 'healthy' | 'warning' | 'error' | 'critical';
  next_run: string | null;
  next_run_timestamp: number | null;
  next_run_in_seconds: number | null;
  last_run: string | null;
  last_run_timestamp: number | null;
  last_run_ago_seconds: number | null;
  last_run_success: boolean;
  last_error: string;
  overdue: boolean;
  schedule_name: string | null;
  schedule_interval: number | null;
  expected_interval: number;
}

interface GuardianStatus {
  installed: boolean;
  active: boolean;
  last_check: string | null;
  last_check_ago: number | null;
  recent_fixes: string[];
}

interface WordPressCronStatus {
  enabled: boolean;
  disabled: boolean;
  alternate: boolean;
  total_events: number;
}

interface CronStatusData {
  overall_health: boolean;
  cron_jobs: CronJob[];
  guardian: GuardianStatus;
  wordpress_cron: WordPressCronStatus;
  timestamp: string;
  timestamp_utc: string;
}

export function CronMonitoringPage() {
  const queryClient = useQueryClient();
  const [autoRefresh, setAutoRefresh] = useState(true);

  // Fetch cron status
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['cron-status'],
    queryFn: async () => {
      const response = await apiClient.request<{success: boolean; data: CronStatusData}>(
        '/wp-json/redd-miner-leads/v1/cron-status'
      );
      return response.data;
    },
    refetchInterval: autoRefresh ? 30000 : false, // Refresh every 30 seconds if enabled
  });

  // Fix cron mutation
  const fixCronMutation = useMutation({
    mutationFn: async (hook?: string) => {
      const response = await apiClient.request(
        '/wp-json/redd-miner-leads/v1/cron-fix',
        {
          method: 'POST',
          body: JSON.stringify({ hook }),
        }
      );
      return response;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['cron-status'] });
    },
  });

  // Trigger cron mutation
  const triggerCronMutation = useMutation({
    mutationFn: async (hook: string) => {
      const response = await apiClient.request(
        '/wp-json/redd-miner-leads/v1/cron-trigger',
        {
          method: 'POST',
          body: JSON.stringify({ hook }),
        }
      );
      return response;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['cron-status'] });
    },
  });

  // Format time difference
  const formatTimeAgo = (seconds: number | null): string => {
    if (!seconds) return 'Never';
    if (seconds < 0) return 'In the future';

    if (seconds < 60) return `${seconds}s ago`;
    if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
    if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
    return `${Math.floor(seconds / 86400)}d ago`;
  };

  const formatTimeUntil = (seconds: number | null): string => {
    if (!seconds) return 'Not scheduled';
    if (seconds < 0) return 'Overdue';

    if (seconds < 60) return `${seconds}s`;
    if (seconds < 3600) return `${Math.floor(seconds / 60)}m`;
    if (seconds < 86400) return `${Math.floor(seconds / 3600)}h`;
    return `${Math.floor(seconds / 86400)}d`;
  };

  // Health status styling
  const getHealthColor = (health: string): string => {
    switch (health) {
      case 'healthy': return 'text-green-600 bg-green-50';
      case 'warning': return 'text-yellow-600 bg-yellow-50';
      case 'error': return 'text-orange-600 bg-orange-50';
      case 'critical': return 'text-red-600 bg-red-50';
      default: return 'text-gray-600 bg-gray-50';
    }
  };

  const getHealthIcon = (health: string) => {
    switch (health) {
      case 'healthy': return <CheckCircle className="w-5 h-5" />;
      case 'warning': return <AlertCircle className="w-5 h-5" />;
      case 'error': return <XCircle className="w-5 h-5" />;
      case 'critical': return <XCircle className="w-5 h-5" />;
      default: return <Clock className="w-5 h-5" />;
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <h2 className="text-red-800 font-semibold mb-2">Error Loading Cron Status</h2>
          <p className="text-red-600">{error.message}</p>
        </div>
      </div>
    );
  }

  if (!data) return null;

  const criticalIssues = data.cron_jobs.filter(job => job.critical && !job.scheduled).length;
  const healthyJobs = data.cron_jobs.filter(job => job.health === 'healthy').length;

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Header */}
        <div className="bg-white rounded-lg shadow-sm p-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
                <Activity className="w-8 h-8 text-blue-600" />
                Cron Monitoring Dashboard
              </h1>
              <p className="text-gray-600 mt-1">Real-time status of all automated background jobs</p>
            </div>
            <div className="flex items-center gap-4">
              <label className="flex items-center gap-2 text-sm text-gray-600">
                <input
                  type="checkbox"
                  checked={autoRefresh}
                  onChange={(e) => setAutoRefresh(e.target.checked)}
                  className="rounded"
                />
                Auto-refresh (30s)
              </label>
              <button
                onClick={() => refetch()}
                className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                <RefreshCw className="w-4 h-4" />
                Refresh Now
              </button>
            </div>
          </div>

          {/* Last updated */}
          <div className="mt-4 text-sm text-gray-500">
            Last updated: {new Date(data.timestamp_utc + 'Z').toLocaleString()}
          </div>
        </div>

        {/* Overall Status Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          {/* Overall Health */}
          <div className={`rounded-lg shadow-sm p-6 ${
            data.overall_health ? 'bg-green-50 border-2 border-green-200' : 'bg-red-50 border-2 border-red-200'
          }`}>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Overall Health</p>
                <p className={`text-2xl font-bold mt-1 ${
                  data.overall_health ? 'text-green-600' : 'text-red-600'
                }`}>
                  {data.overall_health ? 'Healthy' : 'Critical'}
                </p>
              </div>
              {data.overall_health ? (
                <CheckCircle className="w-8 h-8 text-green-600" />
              ) : (
                <XCircle className="w-8 h-8 text-red-600" />
              )}
            </div>
          </div>

          {/* Cron Jobs */}
          <div className="bg-white rounded-lg shadow-sm p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Cron Jobs</p>
                <p className="text-2xl font-bold text-gray-900 mt-1">
                  {healthyJobs}/{data.cron_jobs.length}
                </p>
              </div>
              <Clock className="w-8 h-8 text-blue-600" />
            </div>
            <p className="text-xs text-gray-500 mt-2">Healthy / Total</p>
          </div>

          {/* Critical Issues */}
          <div className={`rounded-lg shadow-sm p-6 ${
            criticalIssues > 0 ? 'bg-red-50 border-2 border-red-200' : 'bg-white'
          }`}>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Critical Issues</p>
                <p className={`text-2xl font-bold mt-1 ${
                  criticalIssues > 0 ? 'text-red-600' : 'text-gray-900'
                }`}>
                  {criticalIssues}
                </p>
              </div>
              <AlertCircle className={`w-8 h-8 ${
                criticalIssues > 0 ? 'text-red-600' : 'text-gray-400'
              }`} />
            </div>
            {criticalIssues > 0 && (
              <button
                onClick={() => fixCronMutation.mutate()}
                className="mt-3 w-full px-3 py-1.5 bg-red-600 text-white text-sm rounded hover:bg-red-700 transition-colors"
              >
                Fix All
              </button>
            )}
          </div>

          {/* Guardian Status */}
          <div className={`rounded-lg shadow-sm p-6 ${
            data.guardian.active ? 'bg-green-50 border-2 border-green-200' : 'bg-yellow-50 border-2 border-yellow-200'
          }`}>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Cron Guardian</p>
                <p className={`text-2xl font-bold mt-1 ${
                  data.guardian.active ? 'text-green-600' : 'text-yellow-600'
                }`}>
                  {data.guardian.active ? 'Active' : 'Inactive'}
                </p>
              </div>
              <Shield className={`w-8 h-8 ${
                data.guardian.active ? 'text-green-600' : 'text-yellow-600'
              }`} />
            </div>
            {data.guardian.last_check && (
              <p className="text-xs text-gray-500 mt-2">
                Last check: {formatTimeAgo(data.guardian.last_check_ago)}
              </p>
            )}
          </div>
        </div>

        {/* Critical Alert Banner */}
        {criticalIssues > 0 && (
          <div className="bg-red-50 border-2 border-red-200 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <AlertCircle className="w-6 h-6 text-red-600 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <h3 className="text-red-900 font-semibold">Critical: {criticalIssues} Cron Job(s) Not Scheduled</h3>
                <p className="text-red-700 mt-1">
                  Core functionality is impaired. Click "Fix All" to reschedule missing cron jobs immediately.
                </p>
              </div>
              <button
                onClick={() => fixCronMutation.mutate()}
                disabled={fixCronMutation.isPending}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 flex items-center gap-2"
              >
                {fixCronMutation.isPending ? (
                  <>
                    <RefreshCw className="w-4 h-4 animate-spin" />
                    Fixing...
                  </>
                ) : (
                  <>
                    <Zap className="w-4 h-4" />
                    Fix All Now
                  </>
                )}
              </button>
            </div>
          </div>
        )}

        {/* Cron Jobs List */}
        <div className="bg-white rounded-lg shadow-sm overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900">Cron Jobs Status</h2>
          </div>
          <div className="divide-y divide-gray-200">
            {data.cron_jobs.map((job) => (
              <div key={job.hook} className="p-6 hover:bg-gray-50 transition-colors">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3">
                      <div className={`flex items-center gap-2 px-3 py-1.5 rounded-full ${getHealthColor(job.health)}`}>
                        {getHealthIcon(job.health)}
                        <span className="text-sm font-medium capitalize">{job.health}</span>
                      </div>
                      <h3 className="text-lg font-semibold text-gray-900">{job.name}</h3>
                      {job.critical && (
                        <span className="px-2 py-0.5 bg-red-100 text-red-700 text-xs font-medium rounded-full">
                          Critical
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-gray-600 mt-1">{job.description}</p>

                    {/* Stats Grid */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-4">
                      {/* Next Run */}
                      <div>
                        <p className="text-xs text-gray-500">Next Run</p>
                        <p className="text-sm font-medium text-gray-900 mt-1">
                          {job.scheduled ? (
                            <>
                              {formatTimeUntil(job.next_run_in_seconds)}
                              <span className="text-xs text-gray-500 ml-1">
                                ({new Date(job.next_run! + ' UTC').toLocaleTimeString()})
                              </span>
                            </>
                          ) : (
                            <span className="text-red-600">Not Scheduled</span>
                          )}
                        </p>
                      </div>

                      {/* Last Run */}
                      <div>
                        <p className="text-xs text-gray-500">Last Run</p>
                        <p className="text-sm font-medium text-gray-900 mt-1">
                          {job.last_run ? (
                            <>
                              {formatTimeAgo(job.last_run_ago_seconds)}
                              {job.last_run_success ? (
                                <CheckCircle className="w-3 h-3 text-green-600 inline ml-1" />
                              ) : (
                                <XCircle className="w-3 h-3 text-red-600 inline ml-1" />
                              )}
                            </>
                          ) : (
                            <span className="text-gray-400">Never</span>
                          )}
                        </p>
                      </div>

                      {/* Schedule */}
                      <div>
                        <p className="text-xs text-gray-500">Schedule</p>
                        <p className="text-sm font-medium text-gray-900 mt-1">
                          {job.schedule_name || 'Unknown'}
                        </p>
                      </div>

                      {/* Status */}
                      <div>
                        <p className="text-xs text-gray-500">Status</p>
                        <p className="text-sm font-medium text-gray-900 mt-1">
                          {job.overdue && <span className="text-red-600">Overdue</span>}
                          {!job.overdue && job.scheduled && <span className="text-green-600">On Schedule</span>}
                          {!job.scheduled && <span className="text-red-600">Missing</span>}
                        </p>
                      </div>
                    </div>

                    {/* Error Message */}
                    {job.last_error && (
                      <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-lg">
                        <p className="text-xs font-medium text-red-900">Last Error:</p>
                        <p className="text-sm text-red-700 mt-1">{job.last_error}</p>
                      </div>
                    )}
                  </div>

                  {/* Actions */}
                  <div className="flex flex-col gap-2 ml-4">
                    {!job.scheduled && (
                      <button
                        onClick={() => fixCronMutation.mutate(job.hook)}
                        disabled={fixCronMutation.isPending}
                        className="px-3 py-1.5 bg-red-600 text-white text-sm rounded hover:bg-red-700 transition-colors disabled:opacity-50"
                      >
                        Fix
                      </button>
                    )}
                    <button
                      onClick={() => triggerCronMutation.mutate(job.hook)}
                      disabled={triggerCronMutation.isPending}
                      className="px-3 py-1.5 bg-blue-600 text-white text-sm rounded hover:bg-blue-700 transition-colors disabled:opacity-50 flex items-center gap-1"
                    >
                      <Zap className="w-3 h-3" />
                      Run Now
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* WordPress Cron System */}
        <div className="bg-white rounded-lg shadow-sm p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">WordPress Cron System</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div>
              <p className="text-sm text-gray-600">System Status</p>
              <p className={`text-lg font-semibold mt-1 ${
                data.wordpress_cron.enabled ? 'text-green-600' : 'text-red-600'
              }`}>
                {data.wordpress_cron.enabled ? 'Enabled' : 'Disabled'}
              </p>
            </div>
            <div>
              <p className="text-sm text-gray-600">Total Events</p>
              <p className="text-lg font-semibold text-gray-900 mt-1">
                {data.wordpress_cron.total_events}
              </p>
            </div>
            <div>
              <p className="text-sm text-gray-600">Alternate Cron</p>
              <p className="text-lg font-semibold text-gray-900 mt-1">
                {data.wordpress_cron.alternate ? 'Yes' : 'No'}
              </p>
            </div>
            <div>
              <p className="text-sm text-gray-600">Guardian Protection</p>
              <p className={`text-lg font-semibold mt-1 ${
                data.guardian.installed ? 'text-green-600' : 'text-yellow-600'
              }`}>
                {data.guardian.installed ? 'Installed' : 'Not Installed'}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
